import { tileAt, type MapDef } from '../world/map';
import { fbm, hash2, hex, mix, noise2, smoothstep, type RGB } from './noise';

/**
 * Ground materials of the xinyi package. Tile chars reserved for it: '1', '2' walkable, '3' solid
 * (solidity is declared in src/world/tiles.ts). Runs inside the ground worker too,
 * so keep it pure: no DOM, no game state. Return null for chars you don't own.
 *
 *   '1' Elephant Mountain stone stairs (wooden deck on the viewpoint platform)
 *   '2' Xinyi plaza paving: big pale granite slabs with darker banding
 *   '3' forested hillside on the mountain; dark roof gravel under building footprints elsewhere
 */
const T = 16;
const scale = (c: RGB, k: number): RGB => [c[0] * k, c[1] * k, c[2] * k];
const C = {
  stone: hex('#a29d92'),
  stoneLight: hex('#c4bfb2'),
  riser: hex('#6e6a62'),
  moss: hex('#5f8a4a'),
  plankA: hex('#a0703e'),
  plankB: hex('#8a5c30'),
  slabA: hex('#d6d1c6'),
  slabB: hex('#c4bfb4'),
  band: hex('#8e8a84'),
  forestDark: hex('#1f4a2a'),
  forestMid: hex('#2f6a36'),
  forestLight: hex('#5a9a48'),
  gravel: hex('#55575c'),
};

const own = (map: MapDef, tx: number, ty: number, ch: string) => tileAt(map, tx, ty) === ch;

/** The viewpoint deck on the summit (taipei map). */
const onDeck = (map: MapDef, x: number, y: number) => map.id === 'taipei' && x >= 85 * T && x < 91 * T && y >= 4 * T && y < 11 * T;

function stairs(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  if (onDeck(map, x, y)) {
    // Wooden planks running east–west with nail heads and weathering.
    const row = Math.floor(y / 4);
    let c = hash2(row, Math.floor((x + row * 23) / 40), 501) < 0.5 ? C.plankA : C.plankB;
    c = scale(c, 0.9 + fbm(x / 30, y / 3, 502) * 0.2);
    if (y % 4 < 0.7) c = scale(c, 0.6);
    if ((x + row * 23) % 40 < 0.7) c = scale(c, 0.7);
    if (((x + row * 23) % 40) > 37 && (y % 4) > 1.5 && (y % 4) < 2.5) c = hex('#3a2a20');
    return c;
  }
  const vert = own(map, tx, ty - 1, '1') || own(map, tx, ty + 1, '1');
  const horiz = own(map, tx - 1, ty, '1') || own(map, tx + 1, ty, '1');
  // Steps across the direction of travel: rows on the north–south runs, columns on the east–west ones.
  const along = vert || !horiz ? y : x;
  const across = along === y ? x : y;
  const step = 6;
  const k = ((along % step) + step) % step;
  const id = Math.floor(along / step);
  let c = mix(C.stone, C.stoneLight, hash2(id, Math.floor(across / 9), 511) * 0.6);
  c = scale(c, 0.92 + noise2(x * 0.7, y * 0.7, 512) * 0.14);
  if (k < 1.2) c = mix(c, C.riser, 0.75);
  else if (k < 2) c = mix(c, C.stoneLight, 0.4);
  // Mossy edges where the forest meets the stairs.
  const fx = x - tx * T, fy = y - ty * T;
  const edgeL = !own(map, tx - 1, ty, '1') && fx < 2.5, edgeR = !own(map, tx + 1, ty, '1') && fx > T - 2.5;
  const edgeT = !own(map, tx, ty - 1, '1') && fy < 2, edgeB = !own(map, tx, ty + 1, '1') && fy > T - 2;
  if (edgeL || edgeR || edgeT || edgeB) c = mix(c, C.moss, 0.55 + noise2(x * 0.5, y * 0.5, 513) * 0.3);
  // Damp patches and leaves blown onto the steps from the forest.
  c = mix(c, hex('#6a6e62'), smoothstep(0.64, 0.82, fbm(x / 30, y / 30, 514)) * 0.3);
  const leaf = leafAt(x, y, 515, 0.05);
  if (leaf) return leaf;
  return c;
}

/** A fallen leaf lying in this 10 px cell, if any. */
function leafAt(x: number, y: number, seed: number, rate: number): RGB | null {
  const S = 10;
  const cx = Math.floor(x / S), cy = Math.floor(y / S);
  const h = hash2(cx, cy, seed);
  if (h > rate) return null;
  const lx = (cx + 0.2 + hash2(cx, cy, seed + 1) * 0.6) * S, ly = (cy + 0.2 + hash2(cx, cy, seed + 2) * 0.6) * S;
  const a = hash2(cx, cy, seed + 3) * Math.PI;
  const dx = x - lx, dy = y - ly;
  const u = dx * Math.cos(a) + dy * Math.sin(a), v = -dx * Math.sin(a) + dy * Math.cos(a);
  if ((u * u) / 7.5 + (v * v) / 1.8 > 1) return null;
  const k = h / rate;
  return k < 0.33 ? hex('#8a5a2a') : k < 0.66 ? hex('#c8a040') : hex('#a0642a');
}

function xinyiPaving(x: number, y: number): RGB {
  // 32 px slabs in a stretcher bond, with a darker band every 4 slabs and a black-granite inlay every 16.
  const s = 32;
  const row = Math.floor(y / (s / 2));
  const off = row % 2 ? s / 2 : 0;
  const u = (((x + off) % s) + s) % s, v = ((y % (s / 2)) + s / 2) % (s / 2);
  const slab = Math.floor((x + off) / s);
  const tone = hash2(slab, row, 521);
  let c = tone < 0.4 ? C.slabA : tone < 0.8 ? C.slabB : hex('#cfc8bc');
  const r8 = ((row % 8) + 8) % 8;
  if (r8 === 3) c = mix(c, C.band, 0.55);
  const r16 = ((row % 16) + 16) % 16;
  if (r16 === 11 && v > 4 && v < 12) c = mix(hex('#4a4a4e'), hex('#5e5a5c'), tone); // black granite inlay strip
  // Drain channel with slots every 16 rows.
  if (r16 === 7 && v > 5 && v < 10.5) c = Math.floor(x / 3) % 2 === 0 ? hex('#3e3e42') : hex('#64625e');
  c = scale(c, 0.95 + noise2(x * 0.9, y * 0.9, 522) * 0.08);
  if (hash2(Math.floor(x * 1.5), Math.floor(y * 1.5), 523) < 0.05) c = scale(c, 0.9);
  if (u < 0.6 || v < 0.6) c = scale(c, 0.8);
  // Worn trails where people walk, chewing-gum spots, and a soft sheen like polished granite after rain.
  c = mix(c, hex('#a8a39a'), smoothstep(0.62, 0.8, fbm(x / 80, y / 40, 525)) * 0.2);
  if (hash2(Math.floor(x / 2), Math.floor(y / 2), 526) < 0.004) c = hex('#8a8680');
  c = mix(c, hex('#ffffff'), smoothstep(0.64, 0.82, fbm(x / 50, y / 22, 524)) * 0.12);
  const leaf = leafAt(x, y, 527, 0.018);
  if (leaf) return leaf;
  return c;
}

function forest(x: number, y: number): RGB {
  // Overlapping round crowns lit from the top-left.
  const s = 11;
  const cx = Math.floor(x / s), cy = Math.floor(y / s);
  let best = 9, lit = 0;
  for (let j = -1; j <= 1; j++) {
    for (let i = -1; i <= 1; i++) {
      const gx = cx + i, gy = cy + j;
      const ox = (gx + 0.5 + (hash2(gx, gy, 531) - 0.5) * 0.8) * s, oy = (gy + 0.5 + (hash2(gx, gy, 532) - 0.5) * 0.8) * s;
      const r = s * (0.7 + hash2(gx, gy, 533) * 0.4);
      const d = Math.hypot(x - ox, y - oy) / r;
      if (d < best) {
        best = d;
        lit = (ox - x + oy - y) / r;
      }
    }
  }
  let c = mix(C.forestDark, C.forestMid, fbm(x / 40, y / 40, 534));
  if (best < 1) c = mix(c, C.forestLight, Math.max(0, lit) * 0.45 * (1 - best * 0.5));
  else c = scale(c, 0.7);
  if (best > 0.85 && best < 1) c = scale(c, 0.78);
  return scale(c, 0.94 + noise2(x * 0.8, y * 0.8, 535) * 0.1);
}

function gravel(x: number, y: number): RGB {
  return scale(C.gravel, 0.88 + hash2(Math.floor(x), Math.floor(y), 541) * 0.2);
}

export function colorAtXinyi(map: MapDef, x: number, y: number, mat: string): RGB | null {
  switch (mat) {
    case '1':
      return stairs(map, x, y);
    case '2':
      return xinyiPaving(x, y);
    case '3':
      return map.id !== 'taipei' || x >= 84 * T ? forest(x, y) : gravel(x, y);
    default:
      return null;
  }
}

/** Flat colours shown while the detailed ground is still rendering. */
export const PLACEHOLDER_XINYI: Record<string, string> = { '1': '#a29d92', '2': '#cfcac0', '3': '#2a5a32' };
